/**
 * Image Format Validation Utilities
 *
 * Provides functions to validate image formats and handle unsupported formats
 * with clear error messages.
 *
 * Uses file-type library for comprehensive format detection from blob magic numbers.
 */

// Import file-type for better format detection (dynamic import used in function to handle browser compatibility)
let fileTypeFromBlob = null;

/**
 * Supported image MIME types that browsers can natively render
 * These are formats that can be loaded into an HTMLImageElement
 */
export const SUPPORTED_IMAGE_MIME_TYPES = [
  // Standard formats
  "image/png",
  "image/jpeg",
  "image/jpg", // Some systems use this instead of image/jpeg
  "image/webp",
  "image/svg+xml",
  "image/x-icon",
  "image/vnd.microsoft.icon",
  "image/x-png", // Non-standard but sometimes used
  "image/x-icon", // Alternative ICO format
  // Additional formats
  "image/avif", // AVIF format
];

/**
 * Supported image file extensions
 */
export const SUPPORTED_IMAGE_EXTENSIONS = [
  ".png",
  ".jpg",
  ".jpeg",
  ".webp",
  ".svg",
  ".ico",
  ".avif",
];

/**
 * Unsupported formats that are commonly encountered (especially from Word)
 * These formats cannot be directly rendered in browsers
 */
export const UNSUPPORTED_FORMATS = {
  "image/tiff": {
    mime: "image/tiff",
    extensions: [".tiff", ".tif"],
  },
  "image/wmf": {
    mime: "image/wmf",
    extensions: [".wmf"],
  },
  "image/emf": {
    mime: "image/emf",
    extensions: [".emf"],
  },
  "image/gif": {
    mime: "image/gif",
    extensions: [".gif"],
  },
  "image/bmp": {
    mime: "image/bmp",
    extensions: [".bmp"],
  },
  "image/x-ms-bmp": {
    mime: "image/x-ms-bmp",
    extensions: [".bmp"],
  },
  "image/heic": {
    mime: "image/heic",
    extensions: [".heic"],
  },
  "image/heif": {
    mime: "image/heif",
    extensions: [".heif"],
  },
  "image/apng": {
    mime: "image/apng",
    extensions: [".apng"],
  },
};

/**
 * Check if a file type is a supported image format
 * @param {string} mimeType - MIME type of the file
 * @param {string} fileName - Name of the file (optional, for extension fallback)
 * @returns {boolean} True if the format is supported
 */
export const isSupportedImageFormat = (mimeType, fileName = "") => {
  // Normalize MIME type (handle case variations and non-standard types)
  const normalizedMime = mimeType?.toLowerCase().trim();

  // Check against supported MIME types
  if (
    normalizedMime &&
    SUPPORTED_IMAGE_MIME_TYPES.some(
      (supported) =>
        normalizedMime === supported.toLowerCase() ||
        normalizedMime.startsWith(supported.toLowerCase())
    )
  ) {
    return true;
  }

  // Fallback: check file extension if MIME type is missing or generic
  if (fileName) {
    const lowerFileName = fileName.toLowerCase();
    const hasSupportedExtension = SUPPORTED_IMAGE_EXTENSIONS.some((ext) =>
      lowerFileName.endsWith(ext)
    );
    if (hasSupportedExtension) {
      return true;
    }
  }

  // Check if it's a generic "image/*" type (browser might not know specific type)
  if (normalizedMime === "image/*" || normalizedMime?.startsWith("image/")) {
    // If we can't determine, allow it and let the browser try to load it
    // The actual validation will happen when trying to load the image
    return true;
  }

  return false;
};

/**
 * Check if a file type is explicitly unsupported
 * @param {string} mimeType - MIME type of the file
 * @param {string} fileName - Name of the file (optional)
 * @returns {Object|null} Unsupported format info or null if not unsupported
 */
export const getUnsupportedFormatInfo = (mimeType, fileName = "") => {
  const normalizedMime = mimeType?.toLowerCase().trim();

  // Check against known unsupported formats
  for (const [key, formatInfo] of Object.entries(UNSUPPORTED_FORMATS)) {
    if (normalizedMime === formatInfo.mime.toLowerCase()) {
      return formatInfo;
    }
  }

  // Check by file extension
  if (fileName) {
    const lowerFileName = fileName.toLowerCase();
    for (const [key, formatInfo] of Object.entries(UNSUPPORTED_FORMATS)) {
      if (formatInfo.extensions.some((ext) => lowerFileName.endsWith(ext))) {
        return formatInfo;
      }
    }
  }

  return null;
};

/**
 * Get a user-friendly error message for unsupported formats
 * @param {string} mimeType - MIME type of the file
 * @param {string} fileName - Name of the file (optional)
 * @returns {string} Error message
 */
export const getUnsupportedFormatMessage = (mimeType, fileName = "") => {
  const displayName = fileName || "this file";
  return `${displayName} is not a supported image. Please Upload a JPEG, PNG, WEBP, ICO, AVIF, or SVG File.`;
};

/**
 * Validate an image file and return validation result
 * @param {File} file - File object to validate
 * @returns {Object} Validation result with { isValid: boolean, error: string|null }
 */
export const validateImageFile = (file) => {
  if (!file) {
    return {
      isValid: false,
      error: "No file provided.",
    };
  }

  // Check for explicitly unsupported formats first
  const unsupportedInfo = getUnsupportedFormatInfo(file.type, file.name);
  if (unsupportedInfo) {
    return {
      isValid: false,
      error: getUnsupportedFormatMessage(file.type, file.name),
    };
  }

  // Check if it's a supported format
  if (!isSupportedImageFormat(file.type, file.name)) {
    return {
      isValid: false,
      error: getUnsupportedFormatMessage(file.type, file.name),
    };
  }

  return {
    isValid: true,
    error: null,
  };
};

/**
 * Get list of supported formats as a readable string
 * @returns {string} Comma-separated list of supported formats
 */
export const getSupportedFormatsList = () => {
  return "PNG, JPEG, WebP, SVG, ICO, AVIF";
};

/**
 * Get the accept attribute string for file input
 * This restricts the file picker to only show supported image formats
 * @returns {string} Comma-separated list of MIME types and extensions for accept attribute
 */
export const getSupportedImageAcceptString = () => {
  // Filter out duplicates and non-standard MIME types that might cause issues
  const uniqueMimeTypes = [
    "image/png",
    "image/jpeg",
    "image/webp",
    "image/svg+xml",
    "image/x-icon",
    "image/vnd.microsoft.icon",
    "image/avif",
  ];

  // Combine MIME types and extensions
  const acceptParts = [...uniqueMimeTypes, ...SUPPORTED_IMAGE_EXTENSIONS];

  return acceptParts.join(",");
};

/**
 * Validate a data URL image format
 * @param {string} dataUrl - Data URL string (e.g., "data:image/png;base64,...")
 * @returns {Object} Validation result with { isValid: boolean, error: string|null }
 */
export const validateDataUrlImage = (dataUrl) => {
  if (!dataUrl || typeof dataUrl !== "string") {
    return {
      isValid: false,
      error: "Invalid data URL provided.",
    };
  }

  if (!dataUrl.startsWith("data:image/")) {
    return {
      isValid: false,
      error: "Data URL does not appear to be an image.",
    };
  }

  try {
    // Extract MIME type from data URL (format: data:image/png;base64,...)
    const mimeMatch = dataUrl.match(/data:([^;]+)/);
    if (!mimeMatch || !mimeMatch[1]) {
      return {
        isValid: false,
        error: "Could not determine image format from data URL.",
      };
    }

    const mimeType = mimeMatch[1].trim();

    // Check for explicitly unsupported formats
    const unsupportedInfo = getUnsupportedFormatInfo(mimeType);
    if (unsupportedInfo) {
      return {
        isValid: false,
        error: getUnsupportedFormatMessage(mimeType),
      };
    }

    // Check if it's a supported format
    if (!isSupportedImageFormat(mimeType)) {
      return {
        isValid: false,
        error: getUnsupportedFormatMessage(mimeType),
      };
    }

    return {
      isValid: true,
      error: null,
    };
  } catch (error) {
    return {
      isValid: false,
      error: `Error validating data URL: ${error.message}`,
    };
  }
};

/**
 * Detect image format from blob's magic numbers (file signatures)
 * Uses file-type library for comprehensive format detection, with fallback to manual detection
 * This is more reliable than trusting MIME types from clipboard
 * @param {Blob} blob - Blob to detect format from
 * @returns {Promise<string|null>} Detected MIME type or null if cannot detect
 */
export const detectImageFormatFromBlob = async (blob) => {
  // Try using file-type library first (better format detection)
  try {
    // Dynamic import to handle browser compatibility and avoid build issues
    if (!fileTypeFromBlob) {
      const fileTypeModule = await import("file-type");
      fileTypeFromBlob = fileTypeModule.fileTypeFromBlob;
    }

    if (fileTypeFromBlob) {
      const fileType = await fileTypeFromBlob(blob);
      if (fileType && fileType.mime) {
        return fileType.mime;
      }
    }
  } catch (error) {
    // file-type might not be available or fail, fall back to manual detection
    fileTypeFromBlob = null; // Reset to try again next time
  }

  // Fallback to manual detection (existing logic)
  try {
    if (!blob || blob.size === 0) {
      console.warn("detectImageFormatFromBlob: Empty or invalid blob");
      return null;
    }

    // Read first 32 bytes (enough for most format signatures, including TIFF)
    // Some formats need more bytes for reliable detection
    // For TIFF, we need at least 4 bytes, but reading more helps with detection
    const bytesToRead = Math.min(32, blob.size);
    const arrayBuffer = await blob.slice(0, bytesToRead).arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);

    if (uint8Array.length < 4) {
      console.warn(
        "detectImageFormatFromBlob: Not enough bytes to detect format",
        uint8Array.length
      );
      return null;
    }

    // Debug: Log first few bytes for troubleshooting

    // TIFF: "II" (0x49 0x49) or "MM" (0x4D 0x4D) followed by 42 (0x2A 0x00 or 0x00 0x2A)
    // TIFF files can be little-endian (II) or big-endian (MM)
    // Little-endian: II* (0x49 0x49 0x2A 0x00)
    // Big-endian: MM* (0x4D 0x4D 0x00 0x2A)
    // CRITICAL: Check TIFF FIRST before other formats, as it's unsupported
    const isLittleEndianTIFF =
      uint8Array[0] === 0x49 &&
      uint8Array[1] === 0x49 &&
      uint8Array[2] === 0x2a &&
      uint8Array[3] === 0x00;
    const isBigEndianTIFF =
      uint8Array[0] === 0x4d &&
      uint8Array[1] === 0x4d &&
      uint8Array[2] === 0x00 &&
      uint8Array[3] === 0x2a;

    if (isLittleEndianTIFF || isBigEndianTIFF) {
      console.warn(
        "🚫 detectImageFormatFromBlob: Detected TIFF format (UNSUPPORTED)"
      );
      return "image/tiff";
    }

    // BMP: "BM" (0x42 0x4D)
    if (uint8Array[0] === 0x42 && uint8Array[1] === 0x4d) {
      return "image/bmp";
    }

    // GIF: "GIF87a" or "GIF89a" (0x47 0x49 0x46 0x38)
    if (
      uint8Array[0] === 0x47 &&
      uint8Array[1] === 0x49 &&
      uint8Array[2] === 0x46 &&
      uint8Array[3] === 0x38
    ) {
      return "image/gif";
    }

    // PNG: 0x89 0x50 0x4E 0x47 0x0D 0x0A 0x1A 0x0A
    if (
      uint8Array[0] === 0x89 &&
      uint8Array[1] === 0x50 &&
      uint8Array[2] === 0x4e &&
      uint8Array[3] === 0x47 &&
      uint8Array[4] === 0x0d &&
      uint8Array[5] === 0x0a &&
      uint8Array[6] === 0x1a &&
      uint8Array[7] === 0x0a
    ) {
      // Check if it's APNG (animated PNG) - APNG has "acTL" chunk
      // For now, we'll detect APNG by checking if it's PNG and has animation
      // This is a simplified check - full APNG detection would require parsing chunks
      // Since APNG is unsupported, we'll treat all PNGs as potentially APNG if we can't verify
      // For clipboard, we'll be conservative and allow PNG (APNG detection is complex)
      return "image/png";
    }

    // JPEG: 0xFF 0xD8 0xFF
    if (
      uint8Array[0] === 0xff &&
      uint8Array[1] === 0xd8 &&
      uint8Array[2] === 0xff
    ) {
      return "image/jpeg";
    }

    // WebP: "RIFF" (0x52 0x49 0x46 0x46) followed by "WEBP" at offset 8
    if (
      uint8Array.length >= 12 &&
      uint8Array[0] === 0x52 &&
      uint8Array[1] === 0x49 &&
      uint8Array[2] === 0x46 &&
      uint8Array[3] === 0x46 &&
      uint8Array[8] === 0x57 &&
      uint8Array[9] === 0x45 &&
      uint8Array[10] === 0x42 &&
      uint8Array[11] === 0x50
    ) {
      return "image/webp";
    }

    // SVG: Check if it starts with XML declaration or <svg tag (text-based format)
    // We need to read more bytes and check if it's valid XML/SVG
    if (uint8Array.length >= 4) {
      const textDecoder = new TextDecoder();
      const text = textDecoder.decode(
        uint8Array.slice(0, Math.min(100, uint8Array.length))
      );
      const trimmedText = text.trim();
      if (
        trimmedText.startsWith("<?xml") ||
        trimmedText.startsWith("<svg") ||
        trimmedText.startsWith("<!DOCTYPE svg")
      ) {
        return "image/svg+xml";
      }
    }

    // WMF/EMF: These are Windows metafile formats, harder to detect from magic numbers alone
    // WMF typically starts with specific headers, but detection is complex
    // EMF is even more complex
    // For now, we'll rely on MIME type for these

    // HEIC/HEIF: These use ftyp box structure, starting with file size (4 bytes) then "ftyp"
    // Detection is more complex, but we can check for "ftyp" at offset 4
    if (uint8Array.length >= 12) {
      const textDecoder = new TextDecoder();
      const ftypCheck = textDecoder.decode(uint8Array.slice(4, 8));
      if (ftypCheck === "ftyp") {
        // Check for HEIC/HEIF brand
        const brand = textDecoder.decode(uint8Array.slice(8, 12));
        if (
          brand.includes("heic") ||
          brand.includes("heif") ||
          brand.includes("mif1")
        ) {
          return "image/heic";
        }
      }
    }

    return null; // Cannot detect format
  } catch (error) {
    console.error("Error detecting image format from blob:", error);
    return null;
  }
};

/**
 * Validate an image URL by checking its extension pattern
 * Note: This only validates the URL pattern, not the actual image format.
 * Full validation requires fetching the image, which happens in addImage's error handler.
 * @param {string} url - Image URL string
 * @returns {Object} Validation result with { isValid: boolean, error: string|null, canValidate: boolean }
 */
export const validateImageUrl = (url) => {
  if (!url || typeof url !== "string") {
    return {
      isValid: false,
      error: "Invalid URL provided.",
      canValidate: false,
    };
  }

  // For blob URLs, we can't validate without the original blob
  if (url.startsWith("blob:")) {
    return {
      isValid: true, // Allow blob URLs, validation will happen on load
      error: null,
      canValidate: false,
    };
  }

  // For HTTP/HTTPS URLs, check extension pattern
  if (url.startsWith("http://") || url.startsWith("https://")) {
    // Extract file extension from URL
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname.toLowerCase();

      // Check for unsupported extensions
      for (const [key, formatInfo] of Object.entries(UNSUPPORTED_FORMATS)) {
        if (formatInfo.extensions.some((ext) => pathname.endsWith(ext))) {
          return {
            isValid: false,
            error: formatInfo.message,
            canValidate: true,
          };
        }
      }

      // Check if it has a supported extension pattern
      const hasSupportedExtension = SUPPORTED_IMAGE_EXTENSIONS.some((ext) =>
        pathname.endsWith(ext)
      );

      if (hasSupportedExtension) {
        return {
          isValid: true,
          error: null,
          canValidate: true,
        };
      }

      // If no extension, allow it but note that validation will happen on load
      return {
        isValid: true,
        error: null,
        canValidate: false,
      };
    } catch (error) {
      // Invalid URL format, but allow it - validation will happen on load
      return {
        isValid: true,
        error: null,
        canValidate: false,
      };
    }
  }

  // For other URL types, allow but note validation will happen on load
  return {
    isValid: true,
    error: null,
    canValidate: false,
  };
};

/**
 * Validate image blob by detecting format from file contents (magic numbers)
 * This validates the actual file contents, not just MIME type or extension
 * Uses file-type library for comprehensive format detection, with manual fallback
 *
 * IMPORTANT LIMITATION: If the OS converts an unsupported format (e.g., TIFF) to a supported format
 * (e.g., PNG) during clipboard copy, both the MIME type and blob bytes will be converted.
 * In such cases, we cannot detect the original format - this is a fundamental limitation
 * of clipboard operations in browsers. For strict format validation, use file upload instead.
 *
 * @param {Blob|File} blob - Blob or File object to validate
 * @param {string} fileName - Optional file name for better error messages
 * @returns {Promise<Object>} Validation result with { isValid: boolean, error: string|null, detectedMimeType: string|null }
 */
export const validateImageBlobContent = async (blob, fileName = "") => {
  try {
    if (!blob || blob.size === 0) {
      return {
        isValid: false,
        error: "Empty or invalid image file provided.",
        detectedMimeType: null,
      };
    }

    // Detect format from file contents using magic numbers (file signatures)
    // This is more reliable than trusting MIME types from clipboard
    // CRITICAL: Always check file contents first, before trusting reported MIME type
    const detectedMimeType = await detectImageFormatFromBlob(blob);


    // Validate the detected format FIRST (file contents take priority over reported MIME type)
    if (detectedMimeType) {
      // Check if detected format is unsupported - REJECT IMMEDIATELY
      // This catches TIFF even if clipboard reports it as PNG
      const unsupportedInfo = getUnsupportedFormatInfo(
        detectedMimeType,
        fileName
      );
      if (unsupportedInfo) {
        console.warn(
          "❌ Rejecting unsupported format detected from file contents:",
          detectedMimeType
        );
        return {
          isValid: false,
          error: getUnsupportedFormatMessage(detectedMimeType, fileName),
          detectedMimeType,
        };
      }

      // Check if detected format is supported
      if (!isSupportedImageFormat(detectedMimeType, fileName)) {
        return {
          isValid: false,
          error: getUnsupportedFormatMessage(detectedMimeType, fileName),
          detectedMimeType,
        };
      }

      // Format is supported
      return {
        isValid: true,
        error: null,
        detectedMimeType,
      };
    }

    // If we couldn't detect the format from contents, check the reported MIME type
    // But be more strict - if we can't detect from contents, it might be suspicious
    const reportedMimeType =
      blob.type || (blob instanceof File ? blob.type : null);
    if (reportedMimeType) {
      // Check if reported type is unsupported
      const unsupportedInfo = getUnsupportedFormatInfo(
        reportedMimeType,
        fileName
      );
      if (unsupportedInfo) {
        return {
          isValid: false,
          error: getUnsupportedFormatMessage(reportedMimeType, fileName),
          detectedMimeType: reportedMimeType,
        };
      }

      // If reported type is generic (like "image/*"), reject it
      // We need to know the actual format
      if (
        reportedMimeType === "image/*" ||
        !reportedMimeType.includes("/") ||
        reportedMimeType.split("/").length < 2
      ) {
        return {
          isValid: false,
          error: `Cannot determine image format from clipboard. Please use a supported format: ${getSupportedFormatsList()}`,
          detectedMimeType: null,
        };
      }

      // Check if reported format is supported
      if (!isSupportedImageFormat(reportedMimeType, fileName)) {
        return {
          isValid: false,
          error: getUnsupportedFormatMessage(reportedMimeType, fileName),
          detectedMimeType: reportedMimeType,
        };
      }

      // Reported type is supported, but we couldn't verify from contents
      // This is less ideal, but we'll allow it with a warning
      console.warn(
        "validateImageBlobContent: Could not verify format from contents, using reported type:",
        reportedMimeType
      );
      return {
        isValid: true,
        error: null,
        detectedMimeType: reportedMimeType,
      };
    }

    // If we can't determine the format at all, reject it
    // This prevents unsupported formats from being pasted
    return {
      isValid: false,
      error: `Cannot determine image format. Please ensure the image is in a supported format: ${getSupportedFormatsList()}`,
      detectedMimeType: null,
    };
  } catch (error) {
    console.error("Error validating image blob content:", error);
    return {
      isValid: false,
      error: `Error validating image: ${error.message}`,
      detectedMimeType: null,
    };
  }
};
